/**
 * WordPack Registry System
 * Auto-discovers and loads all wordpack files using webpack's require.context
 */

import { WordPack } from '../../types/GameTypes';

// Add webpack require.context typing
declare const require: {
  context: (
    directory: string,
    useSubdirectories: boolean,
    regExp: RegExp
  ) => {
    keys(): string[];
    (id: string): any;
  };
};

// Registry for all discovered word packs
const registeredPacks: Record<string, WordPack> = {};

// Flag to track if packs have been loaded
let packsLoaded = false;

/**
 * Auto-discover and load all wordpack files
 * Uses webpack's require.context to dynamically load all .ts files in this directory
 */
function loadAllWordPacks(): void {
  try {
    // Use webpack's require.context to get all .ts files in this directory
    // Regex excludes index.ts to avoid self-import
    const packContext = getRequireContext();
    
    packContext.keys().forEach((path: string) => {
      try {
        const module = packContext(path);
        const pack = module.default;
        
        // Validate and register the pack
        if (isValidWordPack(pack)) {
          registeredPacks[pack.id] = pack;
          console.log(`Registered word pack: ${pack.id} (${pack.name})`);
        } else {
          console.warn(`Invalid word pack in ${path}:`, pack);
        }
      } catch (error) {
        console.error(`Failed to load word pack from ${path}:`, error);
      }
    });
    
    packsLoaded = true;
    console.log(`Loaded ${Object.keys(registeredPacks).length} word packs`);
  } catch (error) {
    console.error('Failed to load word packs:', error);
    packsLoaded = true; // Set to true to prevent infinite retry
  }
}

/**
 * Get the require.context function (extracted for testability)
 */
function getRequireContext() {
  return require.context('./', false, /^\.\/(?!index\.ts$).*\.ts$/);
}

/**
 * Validate that an object meets the WordPack interface requirements
 * @param pack - Object to validate
 * @returns true if valid WordPack, false otherwise
 */
function isValidWordPack(pack: any): pack is WordPack {
  return (
    pack &&
    typeof pack === 'object' &&
    typeof pack.id === 'string' &&
    pack.id.length > 0 &&
    typeof pack.name === 'string' &&
    pack.name.length > 0 &&
    typeof pack.description === 'string' &&
    ['easy', 'medium', 'hard'].includes(pack.difficulty) &&
    Array.isArray(pack.words) &&
    pack.words.length > 0 &&
    pack.words.every((word: any) => typeof word === 'string' && word.length > 0)
  );
}

// Default word pack ID
export const DEFAULT_WORD_PACK = 'all-words';

/**
 * Get available word packs
 * Automatically loads packs if not already loaded
 */
export function getAvailableWordPacks(): Record<string, WordPack> {
  if (!packsLoaded) {
    loadAllWordPacks();
  }
  return { ...registeredPacks };
}

/**
 * Get a word pack by ID
 * @param id - The pack ID to retrieve
 * @returns WordPack or null if not found
 */
export function getWordPack(id: string): WordPack | null {
  if (!packsLoaded) {
    loadAllWordPacks();
  }
  return registeredPacks[id] || null;
}